/**
 * OperationDetailDrawer — right-side slide-over showing all metadata of a
 * single operation: details, audit timeline, documents, assigned provider,
 * available transitions (live from backend), and quick actions.
 *
 * Rule: NEVER hardcode states/transitions. All gating is driven by
 * `stateMachine` passed in.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  X, Clock, FileText, User, Truck, History, RefreshCw, UserCog, Loader2,
  Copy, CreditCard, Phone, Banknote, MapPin, Mail, MessageCircle,
} from 'lucide-react';
import {
  getOperation, operationAudit, listDocuments, transitionOperation,
  candidatesFor, assignProvider,
} from '@/services/workflowApi';
import StateBadge from './StateBadge';
import TransitionControls from './TransitionControls';
import DocumentList from './DocumentList';
import DocumentUploadButton from './DocumentUploadButton';
import { opTypeLabelEs } from './labels';

export const OperationDetailDrawer = ({ operationId, stateMachines, onClose, onChanged }) => {
  const { getAuthHeaders, user } = useAuth();
  const [op, setOp] = useState(null);
  const [audit, setAudit] = useState([]);
  const [docs, setDocs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [tab, setTab] = useState('timeline');
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'admin';

  const load = async () => {
    if (!operationId) return;
    setLoading(true);
    try {
      const [o, a, d] = await Promise.all([
        getOperation(getAuthHeaders, operationId),
        operationAudit(getAuthHeaders, operationId),
        listDocuments(getAuthHeaders, { operation_id: operationId }),
      ]);
      setOp(o);
      setAudit(a.events || []);
      setDocs(d.documents || []);
    } catch (e) {
      toast.error('No se pudo cargar la operación');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [operationId]);

  const handleTransition = async (targetState, note) => {
    try {
      await transitionOperation(getAuthHeaders, operationId, targetState, note);
      toast.success(`Transición → ${targetState}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transición rechazada');
      throw e;
    }
  };

  const loadCandidates = async () => {
    try {
      const r = await candidatesFor(getAuthHeaders, operationId);
      setCandidates(r.candidates || []);
    } catch (e) {
      toast.error('No se pudieron cargar candidatos');
    }
  };

  const handleAssign = async (providerId) => {
    try {
      await assignProvider(getAuthHeaders, operationId, providerId);
      toast.success('Proveedor asignado');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo asignar');
    }
  };

  if (!operationId) return null;

  const sm = op ? stateMachines?.[op.type] : null;
  const assigned = op?.payment_agent_id || op?.ticket_provider_id;

  return (
    <div className="fixed inset-0 z-40" data-testid="operation-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <Card className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white rounded-none shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b flex items-center justify-between bg-slate-50">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Operación</p>
            <h2 className="text-base font-bold text-slate-900 truncate" data-testid="drawer-op-id">{op?.id || '—'}</h2>
            <div className="mt-1 flex items-center gap-2">
              {op?.type && <span className="text-[10px] uppercase font-bold text-slate-500">{opTypeLabelEs(op.type)}</span>}
              {op?.state && <StateBadge state={op.state} />}
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-500 hover:text-slate-900 rounded" data-testid="drawer-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-3 border-b flex gap-1">
          {[
            { id: 'timeline', label: 'Línea de tiempo', icon: History },
            { id: 'details', label: 'Detalles', icon: FileText },
            { id: 'documents', label: `Documentos (${docs.length})`, icon: FileText },
            isAdmin && { id: 'assign', label: 'Asignar', icon: UserCog },
          ].filter(Boolean).map(({ id, label, icon: Ic }) => (
            <button
              key={id}
              onClick={() => { setTab(id); if (id === 'assign' && candidates.length === 0) loadCandidates(); }}
              data-testid={`drawer-tab-${id}`}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg flex items-center gap-1.5 ${
                tab === id ? 'bg-white text-blue-700 border border-b-white border-slate-200' : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <Ic className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
          ) : !op ? (
            <p className="text-sm text-slate-500">Sin datos</p>
          ) : (
            <>
              {tab === 'timeline' && (
                <div className="space-y-2" data-testid="drawer-timeline">
                  {audit.length === 0 ? <p className="text-xs text-slate-500 italic">Sin eventos</p> :
                    audit.map((e) => (
                      <div key={e.id} className="flex items-start gap-3 text-xs">
                        <div className="w-2 h-2 mt-1.5 rounded-full bg-blue-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-800">{e.action}</span>
                            <span className="text-slate-400">·</span>
                            <span className="text-slate-500">{e.actor_role}</span>
                            {e.previous_state && e.new_state && (
                              <>
                                <span className="text-slate-400">·</span>
                                <span className="font-mono text-[10px]">{e.previous_state} → {e.new_state}</span>
                              </>
                            )}
                          </div>
                          {e.metadata && Object.keys(e.metadata).length > 0 && (
                            <pre className="mt-1 text-[10px] text-slate-500 whitespace-pre-wrap font-mono">{JSON.stringify(e.metadata, null, 0)}</pre>
                          )}
                          <p className="text-[10px] text-slate-400 mt-0.5">{new Date(e.timestamp).toLocaleString()}</p>
                        </div>
                      </div>
                    ))
                  }
                </div>
              )}

              {tab === 'details' && (
                <div className="space-y-3 text-xs" data-testid="drawer-details">
                  {op.type === 'remittance' && (
                    <RemittancePaymentBlock op={op} />
                  )}
                  <Row label="Tipo"          value={opTypeLabelEs(op.type)} />
                  <Row label="Estado"        value={<StateBadge state={op.state} size="lg" />} />
                  <Row label="Creado por"    value={
                    op.created_by_info ? (
                      <span>
                        <b>{op.created_by_info.name}</b>
                        <span className="text-slate-400 ml-1">({op.created_by_info.role})</span>
                        {op.created_by_info.email && (
                          <a href={`mailto:${op.created_by_info.email}`} className="ml-2 text-blue-600 hover:underline inline-flex items-center gap-1">
                            <Mail className="w-3 h-3" />{op.created_by_info.email}
                          </a>
                        )}
                      </span>
                    ) : `${op.created_by_role || '—'} · ${op.created_by?.slice(0,8) || '—'}`
                  } />
                  {op.gestor_info ? (
                    <Row label="Gestor" value={
                      <span>
                        <b>{op.gestor_info.name}</b>
                        {op.gestor_info.email && (
                          <a href={`mailto:${op.gestor_info.email}`} className="ml-2 text-blue-600 hover:underline inline-flex items-center gap-1">
                            <Mail className="w-3 h-3" />{op.gestor_info.email}
                          </a>
                        )}
                      </span>
                    } />
                  ) : op.gestor_id ? (
                    <Row label="Gestor" value={op.gestor_id.slice(0,8)} />
                  ) : null}
                  <Row label="Cliente"       value={op.client_id?.slice(0,8) || '—'} />
                  <Row label="Monto enviado" value={op.amount ? `${op.amount} ${op.currency_from || ''}` : '—'} />
                  {op.client_payout_amount != null && (
                    <Row label="Cliente recibe" value={
                      <b className="text-emerald-700 text-sm font-mono">
                        {Number(op.client_payout_amount).toLocaleString('es-ES', { minimumFractionDigits: 2 })} {op.client_payout_currency}
                      </b>
                    } />
                  )}
                  <Row label="Ruta"          value={op.route_origin && op.route_destination ? `${op.route_origin} → ${op.route_destination}` : '—'} />
                  {op.passenger_names?.length > 0 && <Row label="Pasajeros" value={op.passenger_names.join(', ')} />}
                  <Row label="Creado"        value={new Date(op.created_at).toLocaleString()} />
                  <Row label="Actualizado"   value={new Date(op.updated_at).toLocaleString()} />
                  {assigned && <Row label="Proveedor asignado" value={<span className="font-mono">{assigned.slice(0,8)}…</span>} />}

                  <div className="pt-3 border-t">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Transiciones disponibles</p>
                    <TransitionControls stateMachine={sm} currentState={op.state} onTransition={handleTransition} />
                  </div>
                </div>
              )}

              {tab === 'documents' && (
                <div className="space-y-3" data-testid="drawer-documents">
                  <DocumentUploadButton operationId={op.id} getAuthHeaders={getAuthHeaders} onUploaded={load} />
                  <DocumentList
                    documents={docs}
                    getAuthHeaders={getAuthHeaders}
                    onChange={load}
                    canVerify={isAdmin || user?.role === 'payment_agent'}
                    canDelete={isAdmin}
                  />
                </div>
              )}

              {tab === 'assign' && isAdmin && (
                <div className="space-y-3" data-testid="drawer-assign">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-600">Candidatos ranked por score (capacidad, ruta, moneda).</p>
                    <Button size="sm" variant="outline" onClick={loadCandidates}><RefreshCw className="w-3 h-3 mr-1" /> Actualizar</Button>
                  </div>
                  {candidates.length === 0 ? (
                    <p className="text-xs text-slate-500 italic">Sin candidatos disponibles. Crea un proveedor o ajusta sus rutas/monedas.</p>
                  ) : candidates.map((c) => (
                    <div key={c.provider_id} className="flex items-center gap-3 p-3 border rounded-lg bg-white" data-testid={`candidate-${c.provider_id}`}>
                      <div className="w-9 h-9 bg-slate-100 rounded flex items-center justify-center"><Truck className="w-4 h-4 text-slate-600" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{c.provider_name || c.provider_id.slice(0,8)}</p>
                        <p className="text-[10px] text-slate-500">{c.reasons?.join(' · ')}</p>
                      </div>
                      <span className="text-xs font-mono font-bold text-slate-700">{c.score}</span>
                      <Button size="sm" onClick={() => handleAssign(c.provider_id)} className="bg-blue-600 hover:bg-blue-700" data-testid={`candidate-assign-${c.provider_id}`}>
                        Asignar
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="text-slate-500 shrink-0">{label}</span>
    <span className="text-slate-900 text-right break-words">{value}</span>
  </div>
);

// ── Bloque "Datos para pagar" — Proveedor CU ve aquí TODO lo que necesita
// para entregar la remesa: tarjeta completa (con copiar), beneficiario,
// teléfono con link a WhatsApp, banco y monto exacto a pagar al cliente.
const formatCardDigits = (raw) => {
  const d = String(raw || '').replace(/\D+/g, '');
  return d.length > 0 ? d.replace(/(.{4})(?=.)/g, '$1 ') : '';
};

const CopyButton = ({ value, label = 'Copiar', testId }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value || ''));
      setDone(true);
      toast.success(`${label}: copiado`);
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error('No se pudo copiar');
    }
  };
  return (
    <button
      onClick={copy}
      data-testid={testId}
      title={`Copiar ${label}`}
      className={`inline-flex items-center justify-center w-7 h-7 rounded-md border ml-2 transition ${
        done ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-300 hover:bg-slate-100 text-slate-600'
      }`}
    >
      <Copy className="w-3.5 h-3.5" />
    </button>
  );
};

const RemittancePaymentBlock = ({ op }) => {
  const md = op?.metadata || {};
  const cardRaw = String(md.card_number || '').replace(/\D+/g, '');
  const cardFmt = formatCardDigits(cardRaw);
  const phone = String(md.beneficiary_phone || md.client_phone || '').trim();
  const phoneClean = phone.replace(/\D+/g, '');
  const beneficiary = md.beneficiary || md.beneficiary_name || md.client_name || '—';
  const bank = md.card_bank_name || md.beneficiary_bank;
  const payout = op.client_payout_amount ?? md.amount_received;
  const payoutCcy = op.client_payout_currency || op.currency_to;
  const opShort = (op.id || '').slice(0, 8).toUpperCase();
  const waUrl = phoneClean
    ? `https://wa.me/${phoneClean}?text=${encodeURIComponent(
        `Hola ${beneficiary}, tu remesa Aircom Connect ${opShort} está siendo procesada. Te confirmo cuando llegue a tu tarjeta.`
      )}`
    : null;

  return (
    <Card className="p-4 mb-3 border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="provider-payment-block">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 flex items-center gap-1.5">
          <Banknote className="w-3.5 h-3.5" /> Datos para pagar al cliente
        </p>
        <span className="text-[10px] uppercase font-bold font-mono text-slate-500">
          # {opShort}
          <CopyButton value={op.id} label="ID operación" testId="copy-op-id" />
        </span>
      </div>

      {/* Cantidad a pagar — destacada */}
      {payout != null && (
        <div className="mb-3 p-3 bg-white rounded-lg border border-emerald-200" data-testid="provider-payout">
          <p className="text-[10px] uppercase tracking-wide text-slate-500">Pagar al cliente</p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-2xl font-bold font-mono text-emerald-700">
              {Number(payout).toLocaleString('es-ES', { minimumFractionDigits: 2 })}
            </span>
            <span className="text-base font-bold text-emerald-700">{payoutCcy}</span>
          </div>
          {op.amount && (
            <p className="text-[10px] text-slate-500 mt-1">
              Equivalente a {Number(op.amount).toLocaleString('es-ES', { minimumFractionDigits: 2 })} {op.currency_from} a tasa fija
            </p>
          )}
        </div>
      )}

      {/* Tarjeta destino */}
      {cardRaw && (
        <div className="mb-3" data-testid="provider-card-block">
          <p className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
            <CreditCard className="w-3 h-3" /> Tarjeta destino
            {bank && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[9px] font-bold">{bank}</span>}
          </p>
          <div className="flex items-center mt-1">
            <span className="font-mono font-bold text-base text-slate-900 tracking-wider select-all" data-testid="provider-card-number">
              {cardFmt}
            </span>
            <CopyButton value={cardRaw} label="Tarjeta" testId="copy-card-number" />
          </div>
        </div>
      )}

      {/* Beneficiario + teléfono */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
            <User className="w-3 h-3" /> Beneficiario
          </p>
          <p className="text-sm font-semibold text-slate-900 mt-0.5" data-testid="provider-beneficiary">
            {beneficiary}
          </p>
        </div>
        {phone && (
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
              <Phone className="w-3 h-3" /> Teléfono
            </p>
            <div className="flex items-center mt-0.5">
              <a
                href={waUrl || `tel:${phoneClean}`}
                target={waUrl ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className="text-sm font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                data-testid="provider-phone"
              >
                {phone}
                {waUrl && <MessageCircle className="w-3.5 h-3.5" />}
              </a>
              <CopyButton value={phoneClean} label="Teléfono" testId="copy-phone" />
            </div>
          </div>
        )}
      </div>

      {/* Notas del gestor */}
      {md.gestor_notes && (
        <div className="mt-3 p-2 bg-amber-50 border-l-2 border-amber-400 text-[11px] text-amber-900 italic" data-testid="provider-notes">
          📝 {md.gestor_notes}
        </div>
      )}
    </Card>
  );
};

export default OperationDetailDrawer;
